import { useEffect, useMemo, useState } from "react"

import AccessTimeIcon from "@mui/icons-material/AccessTime"
import { Controller, useForm } from "react-hook-form"

import { addMinutes } from "date-fns"
import { useEditBookingMutation } from "pages/bookings/newBookingApi"
import { useParams } from "react-router-dom"
import { TableSpinner } from "shared/ui/dataTable/spinner"
import { toast } from "sonner"
import NotFound from "./404"
import {
  useGetAvailableTimeSlotsQuery,
  useGetBookingByHashQuery,
  useUpdateHashBookingMutation,
} from "./bookingHashApi"
import ChangeStatus from "./changeStatus"
import ChangeDate from "./edit"
import { useConvertToMinutes, useFormatDate } from "./hook"

const currentDate = new Date()

// Функция для форматирования даты в формате DD.MM.YYYY
const formatDateForDisplay = (dateString) => {
  if (!dateString) return ""
  const date = new Date(dateString)
  const day = date.getDate().toString().padStart(2, "0")
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

// CSS стили для исправления проблем с инпутами в iOS
const iOSInputStyles = `
  /* iOS Safari fixes for date and time inputs */
  input[type="date"],
  input[type="time"] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-color: white;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.5;
    color: #374151;
    width: 100%;
    box-sizing: border-box;
    text-align: left;
  }

  /* Remove default iOS styling */
  input[type="date"]::-webkit-datetime-edit,
  input[type="time"]::-webkit-datetime-edit {
    padding: 0;
    text-align: left;
  }

  input[type="date"]::-webkit-calendar-picker-indicator,
  input[type="time"]::-webkit-calendar-picker-indicator {
    background: transparent;
    bottom: 0;
    color: transparent;
    cursor: pointer;
    height: auto;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
  }

  /* Ensure proper touch targets on iOS */
  input[type="date"],
  input[type="time"] {
    min-height: 44px;
    touch-action: manipulation;
  }

  /* Fix for iOS Safari focus states */
  input[type="date"]:focus,
  input[type="time"]:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  /* Disabled state */
  input[type="date"]:disabled,
  input[type="time"]:disabled {
    background-color: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }

  /* iOS specific date format */
  input[type="date"]::-webkit-datetime-edit-fields-wrapper {
    display: flex;
    align-items: center;
    text-align: left;
  }

  input[type="date"]::-webkit-datetime-edit-text {
    color: #374151;
    padding: 0 2px;
    text-align: left;
  }

  input[type="date"]::-webkit-datetime-edit-month-field,
  input[type="date"]::-webkit-datetime-edit-day-field,
  input[type="date"]::-webkit-datetime-edit-year-field {
    color: #374151;
    padding: 0 2px;
    text-align: left;
  }

  /* iOS specific time format */
  input[type="time"]::-webkit-datetime-edit-fields-wrapper {
    display: flex;
    align-items: center;
    text-align: left;
  }

  input[type="time"]::-webkit-datetime-edit-text {
    color: #374151;
    padding: 0 2px;
    text-align: left;
  }

  input[type="time"]::-webkit-datetime-edit-hour-field,
  input[type="time"]::-webkit-datetime-edit-minute-field {
    color: #374151;
    padding: 0 2px;
    text-align: left;
  }

  /* iOS Safari specific date format with dots */
  @supports (-webkit-touch-callout: none) {
    input[type="date"]::-webkit-datetime-edit-text {
      color: #374151;
      padding: 0 1px;
      text-align: left;
    }
    
    input[type="date"]::-webkit-datetime-edit-month-field,
    input[type="date"]::-webkit-datetime-edit-day-field,
    input[type="date"]::-webkit-datetime-edit-year-field {
      color: #374151;
      padding: 0 1px;
      min-width: 1.5em;
      text-align: left;
    }
  }

  /* iOS Safari specific time format with colons */
  @supports (-webkit-touch-callout: none) {
    input[type="time"]::-webkit-datetime-edit-text {
      color: #374151;
      padding: 0 1px;
      text-align: left;
    }
    
    input[type="time"]::-webkit-datetime-edit-hour-field,
    input[type="time"]::-webkit-datetime-edit-minute-field {
      color: #374151;
      padding: 0 1px;
      min-width: 1.5em;
      text-align: left;
    }
  }

  /* Force left alignment for iOS date/time inputs */
  @supports (-webkit-touch-callout: none) {
    input[type="date"],
    input[type="time"] {
      text-align: left !important;
    }
    
    input[type="date"]::-webkit-datetime-edit,
    input[type="time"]::-webkit-datetime-edit {
      text-align: left !important;
    }
    
    input[type="date"]::-webkit-datetime-edit-fields-wrapper,
    input[type="time"]::-webkit-datetime-edit-fields-wrapper {
      text-align: left !important;
      justify-content: flex-start;
    }
  }
`

const ClientConfirmation = ({ refetch: calendarRefetch }) => {
  // * Hooks
  const formatDate = useFormatDate()
  const convert = useConvertToMinutes()
  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
  } = useForm()

  // * API
  const [updateHashBooking, { isSuccess, isError }] =
    useUpdateHashBookingMutation()

  // * State
  const [date, setDate] = useState(null)
  const [time, setTime] = useState("")
  const [services, setServices] = useState([])
  const [showConfirm, setShowConfirm] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [showCancel, setShowCancel] = useState(false)
  const [show404, setShow404] = useState(false)

  const [dateChange, setDateChange] = useState(false)
  const [timeChange, setTimeChange] = useState(false)

  // * Prepayment states
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false)
  const [timeLeft, setTimeLeft] = useState(0)
  const [timeExpired, setTimeExpired] = useState(false)

  // * Other
  const { hash } = useParams()

  // ? Изначальный запрос для получения данных брони по хэшу
  const {
    data: bookingData,
    isLoading: bookingDataLoading,
    error: bookingHashError,
  } = useGetBookingByHashQuery(hash)

  useEffect(() => {
    if (bookingData) {
      setServices(bookingData.services)
    }
  }, [bookingData])

  // ? Сумма всех услуг
  const servicesSum = useMemo(() => {
    return services.length
      ? services.reduce(
          (prev, curr) => prev + Number(curr.price) * (curr.count || 1),
          0
        )
      : 0
  }, [services])

  // ? Суммарное время
  const sumTime = useMemo(() => {
    return services.length
      ? services.reduce(
          (prev, curr) => prev + convert(curr.duration) * (curr.count || 1),
          0
        )
      : 0
  }, [services, convert])

  // ? Расчет предоплаты
  const prepaidAmount = useMemo(() => {
    if (!bookingData?.master?.prepayment_enabled) return 0

    const master = bookingData.master
    if (master.prepayment_type === "percent") {
      return (servicesSum * parseFloat(master.prepayment_value)) / 100
    } else if (master.prepayment_type === "fixed") {
      const fixedAmount = parseFloat(master.prepayment_value) || 0

      // Если сумма предоплаты больше или равна сумме услуг, показываем предоплату равную итогу
      if (fixedAmount >= servicesSum) {
        return servicesSum
      }

      // Если сумма предоплаты меньше суммы услуг, используем её
      return fixedAmount
    }
    return 0
  }, [bookingData?.master, servicesSum])

  // ? Проверка наличия предоплаты
  const hasPrepaid = useMemo(() => {
    if (!bookingData?.master?.prepayment_enabled || prepaidAmount <= 0)
      return false

    return true
  }, [bookingData?.master?.prepayment_enabled, prepaidAmount])

  // ? Расчет оставшегося времени для предоплаты
  const calculateTimeLeft = useMemo(() => {
    if (!bookingData?.booking?.created_at || !hasPrepaid) return 0

    const createdAt = new Date(bookingData.booking.created_at)
    const now = new Date()
    const timePassed = Math.floor((now - createdAt) / 1000) // секунды
    const timeLeft = 15 * 60 - timePassed // 15 минут в секундах

    return Math.max(0, timeLeft) // не может быть отрицательным
  }, [bookingData?.booking?.created_at, hasPrepaid])

  // ? Запрос, чтобы получить доступное время
  // ? Отправляется при выборе даты
  const { data: timeSlotsData, error: timeSlotsError } =
    useGetAvailableTimeSlotsQuery(
      {
        bookingId: bookingData?.booking?.id,
        hash: hash,
        masterId: bookingData?.booking?.master_id,
        serviceDuration: sumTime,
        date: formatDate(date),
      },
      {
        skip: !bookingData || !date || !sumTime,
      }
    )

  useEffect(() => {
    if (bookingData) {
      setDate(new Date(bookingData.booking.start))
      setTime(bookingData.booking.start.slice(-5))
    }
  }, [bookingData])

  // * Functions
  const onDateChange = (date, field) => {
    field.onChange(date)
    if (bookingData.booking.start.slice(0, -6) === formatDate(date)) {
      setDateChange(false)
      setDate(date)
    } else {
      setDateChange(true)
      setDate(date)
    }
  }

  useEffect(() => {
    if (bookingData) {
      if (timeSlotsData) {
        if (bookingData.booking.start.slice(0, -6) === formatDate(date)) {
          setDateChange(false)
        }
        if (bookingData.booking.start.slice(-5) === time) {
          setTimeChange(false)
        }
      }
    }
  }, [date, time, bookingData, setDateChange, formatDate, timeSlotsData])

  const onTimeChange = (time, field) => {
    field.onChange(field)
    setTime(time.target.value)
    setTimeChange(true)
  }

  const currentStatus = bookingData ? bookingData.booking.status : null

  // Функция для получения текста статуса
  const getStatusText = (status) => {
    if (!status) return "неизвестно"

    switch (status.id) {
      case 3:
        return "Отменена"
      case 4:
        return "В ожидании"
      case 6:
        return "Оплачена"
      case 8:
        return "Подтверждена"
      case 11:
        return "Ожидание оплаты"
      default:
        return status.name || "неизвестно"
    }
  }

  // Отладочная информация для статуса
  useEffect(() => {
    if (currentStatus) {
      console.log("Current status:", currentStatus)
      console.log("Status ID:", currentStatus.id)
      console.log("Status name:", currentStatus.name)
      console.log("Status text:", getStatusText(currentStatus))
    }
  }, [currentStatus])

  // Проверяем, есть ли доступное время для выбранной даты
  const hasAvailableTime = useMemo(() => {
    return timeSlotsData && timeSlotsData.length > 0
  }, [timeSlotsData])

  // Проверяем, является ли выбранная дата выходным днем (ошибка 404 с сообщением "Этот день выходной")
  const isDayOff = useMemo(() => {
    return (
      timeSlotsError?.status === 404 &&
      timeSlotsError?.data?.message === "Этот день выходной"
    )
  }, [timeSlotsError])

  // Поле времени должно быть заблокировано, если это выходной, нет доступного времени, или время истекло
  const isTimeDisabled = useMemo(() => {
    return (
      isDayOff ||
      currentStatus?.id === 3 ||
      currentStatus?.id === 6 ||
      timeExpired
    )
  }, [isDayOff, currentStatus, timeExpired])

  const [activeStatusChange, setActiveStatusChange] = useState(false)
  const [freeTime, setFreeTime] = useState([])
  const [formStatus, setFormStatus] = useState("new")
  const [masters, setMasters] = useState([])
  const [updateBooking] = useEditBookingMutation()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const bookingVizit = bookingData?.booking
  const [statusId, setStatusId] = useState(bookingVizit?.status)
  const [salonId, setSalonId] = useState(0)
  const [clientId, setClientId] = useState(null)

  useEffect(() => {
    if (!bookingDataLoading && bookingData) {
      const clientId = bookingData?.booking?.client?.id
      setClientId(clientId)
    }
  }, [bookingDataLoading, bookingData])

  useEffect(() => {
    const salon = JSON.parse(localStorage.getItem("user"))
    setSalonId(salon?.salon?.id)
  }, [])

  useEffect(() => {
    const salon = JSON.parse(localStorage.getItem("user"))
    setSalonId(salon?.salon?.id)
  }, [])

  // Set form values when booking data changes
  useEffect(() => {
    if (bookingData && bookingData.start) {
      const date = new Date(bookingData.start)
      const time = bookingData.start.slice(11, 16)
      setValue("date", date)
      setValue("time", time)
      setSelectedDate(date) // Update selectedDate on bookingData change
    }
  }, [bookingData, setValue])

  useEffect(() => {
    if (bookingData && bookingData.start) {
      setValue("date", new Date(bookingData.start)) // Установка даты
      setValue("time", bookingData.start.slice(11, 16)) // Установка времени
    }
  }, [bookingData, setValue])

  useEffect(() => {
    if (!bookingDataLoading && bookingData) {
      const startDate = bookingData?.booking?.start?.slice(0, 10)
      if (startDate) {
        setValue("date", new Date(startDate))
      }
    }
  }, [bookingDataLoading, bookingData, masters.length, setValue])

  // * Перенос записи
  const editBooking = (data) => {
    // Проверяем доступность времени для выбранной даты
    if (!timeSlotsData || timeSlotsData.length === 0) {
      toast.error(
        "На выбранную дату нет доступного времени. Возможно, это выходной день специалиста."
      )
      return
    }

    // Проверяем, входит ли выбранное время в список доступных слотов
    if (!timeSlotsData.includes(time)) {
      toast.error(
        "Выбранное время недоступно. Пожалуйста, выберите время из списка доступных."
      )
      return
    }

    // * Вычисление конца времени записи
    let end = new Date(`${formatDate(data.date)} ${time}`)

    end = addMinutes(end, sumTime)

    // ? Бэк не примет значения времения 9:00 или 10:1, поэтому нужно добавить ноль в начало
    const minutes =
      end.getMinutes() === 0 ? `${end.getMinutes()}0` : end.getMinutes()
    const hours =
      end.getHours().toString().length === 1
        ? `0${end.getHours()}`
        : end.getHours()

    const submit = {
      start: `${formatDate(data.date)} ${time}`,
      end: `${formatDate(data.date)} ${hours}:${minutes}`,
      // Сохраняем текущий статус при изменении даты/времени
      status_id: currentStatus?.id,
    }

    updateHashBooking({ body: submit, bookingId: bookingData.booking.id, hash })
  }

  // * Подтверждение
  const confirmBooking = () => {
    const submit = {
      status_id: 8,
    }
    updateHashBooking({ body: submit, bookingId: bookingData.booking.id, hash })
  }

  // * Отмена
  const cancelBooking = () => {
    const submit = {
      status_id: 3,
    }

    updateHashBooking({ body: submit, bookingId: bookingData.booking.id, hash })
  }

  // * Оплата с редиректом на Alif Mobi
  const handlePayment = async () => {
    if (isPaymentProcessing) return // Защита от множественных кликов

    setIsPaymentProcessing(true)
    try {
      // Не меняем статус, оставляем как есть - бэкенд сам управляет статусами
      const result = await updateHashBooking({
        body: {},
        bookingId: bookingData.booking.id,
        hash,
      }).unwrap()

      // Получаем booking_id из ответа и перенаправляем на Alif Mobi
      const bookingId = result.booking_id || bookingData.booking.id
      const alifUrl = `https://alifmobi.page.link/providers?id=1234&amount=${prepaidAmount}&account=${bookingId}`

      window.location.href = alifUrl
    } catch (error) {
      console.error("Payment error:", error)
      setIsPaymentProcessing(false) // Разблокируем при ошибке
    }
  }

  useEffect(() => {
    if (bookingHashError) {
      if (bookingHashError?.status === 404) {
        setShow404(true)
      }
    }
  }, [bookingHashError])

  // ? Таймер для предупреждения о предоплате
  useEffect(() => {
    if (!hasPrepaid) {
      setTimeLeft(0)
      setTimeExpired(false)
      return
    }

    // Проверяем, истекло ли время при загрузке
    if (calculateTimeLeft <= 0) {
      setTimeExpired(true)
      setTimeLeft(0)
      return
    }

    // Устанавливаем начальное значение времени
    setTimeLeft(calculateTimeLeft)
    setTimeExpired(false)

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          // НЕ отменяем запись автоматически - пусть бэкенд сам управляет
          setTimeExpired(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [hasPrepaid, calculateTimeLeft])

  // ? Форматирование времени для таймера
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`
  }

  if (bookingDataLoading) {
    return (
      <div className='flex justify-center items-center h-screen w-full'>
        <TableSpinner />
      </div>
    )
  }

  if (bookingHashError) {
    return <NotFound title={"Запись не найдена или истекла"} />
  }

  return (
    <form className=' w-full flex flex-col items-center bigMobile:gap-2 !overflow-y-auto no-scrollbar h-[100vh]  bg-[#FFFFFF]  mobile:mb-4 pb-10'>
      {/* iOS Safari fixes */}
      <style>{iOSInputStyles}</style>

      <div className='text-center  w-full bigMobile:h-[55px] mobile:h-[65px] bg-[#1E3148] text-white bigMobile:p-2 py-4'>
        <h2 className='text-2xl font-semibold'>Ваша запись</h2>
      </div>
      <div className='bg-white p-2  mb-2 w-full max-w-[358px] overflow-y-auto pb-6'>
        <div>
          <div className='flex  bg-[#F6FAFF] h-[52px] justify-between  mx-auto w-full p-2 rounded-lg '>
            <div>
              <p className='text-gray-600 text-small font-normal text-[#A4AFBD] '>
                {bookingData && bookingData?.salon?.salon_type_id === 1
                  ? "Компания"
                  : "Частный специалист"}
              </p>
              <p className='font-medium text-medium '>
                {bookingData?.salon?.name || "N/A"}
              </p>
            </div>
            <div className='text-end'>
              <p className='text-gray-600 text-small font-normal text-[#A4AFBD] '>
                Специалист
              </p>
              <p className='font-medium text-medium'>
                {bookingData?.master?.name || "N/A"}
              </p>
            </div>
          </div>

          <h3 className='text-lg font-semibold mt-4'>Услуги</h3>

          <div className='border-t mt-2 pt-2 border-[#EDEDED] '>
            <div className='overflow-y-auto no-scrollbar h-full min-h-[80px] max-h-[120px] '>
              {bookingData ? (
                bookingData.services.map((service, index) => (
                  <div
                    key={`${service.id}-${index}`}
                    className='flex justify-between py-2 border-b border-[#EDEDED]'
                  >
                    <span>
                      <p className='font-medium text-medium'>
                        {service.name}
                        {service.count > 1 && (
                          <span className='ml-1 text-[#EE6F42]'>
                            x{service.count}
                          </span>
                        )}
                      </p>
                      <small className='text-gray-600 text-small font-normal text-[#A4AFBD]'>
                        {service.upcategory
                          ? service.upcategory.name
                          : "Без категории"}
                      </small>
                    </span>
                    <span className='text-end'>
                      <p className='text-medium'>{service.price} TJS</p>
                      <small className='text-gray-600 text-small font-normal text-[#A4AFBD]'>
                        {Number(service.duration.split(":")[0])
                          ? `${Number(service.duration.split(":")[0])} ЧАС ${Number(service.duration.split(":")[1]) ? Number(service.duration.split(":")[1]) : ""} `
                          : `${Number(service.duration.split(":")[1])} МИН`}
                      </small>
                    </span>
                  </div>
                ))
              ) : (
                <p>No service available</p>
              )}
            </div>
            <div className='flex justify-between font-semibold pt-2  px-2 bg-[#F6FAFF]'>
              <p>Итого</p>

              <span>
                <p className='font-medium text-medium '>{servicesSum} TJS</p>
                <small className='text-gray-600 text-small font-normal text-[#A4AFBD]'>
                  {sumTime} мин
                </small>
              </span>
            </div>

            {/* Блок предоплаты */}
            {hasPrepaid && (
              <div className='bg-[#F6FAFF] px-2'>
                <div className='flex justify-between py-2'>
                  <p className='text-[#176B0D] font-normal'>
                    {bookingData?.master?.prepayment_type === "percent"
                      ? `Предоплата (${Math.round(bookingData?.master?.prepayment_value || 0)}%):`
                      : `Предоплата:`}
                  </p>
                  <span>
                    <p className='text-[#176B0D] font-normal'>
                      {prepaidAmount.toFixed(0)} TJS
                    </p>
                  </span>
                </div>
                <div className='flex justify-between py-2 border-t border-[#E5E7EB]'>
                  <span className='text-gray-600 text-[16px]'>Остаток:</span>
                  <span className='font-bold text-[16px]'>
                    {(servicesSum - prepaidAmount).toFixed(0)} TJS
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className=' max-w-[358px] w-full mx-auto flex flex-col mb-4   justify-between gap-4'>
          <p className='text-gray-600 text-small font-normal text-[#A4AFBD] text-center mt-2 '>
            Вы можете перенести визит, изменив{" "}
            <span className='font-medium'>дату</span> и{" "}
            <span className='font-medium'>время</span>{" "}
          </p>

          <div className='col-span-3 flex flex-col sm:flex-row gap-3 sm:gap-0 justify-between [&>label]:w-full [&>label]:flex [&>label]:flex-col '>
            <label className='label-base sm:mr-2'>
              <span className='font-medium text-main'>Дата</span>
              <div style={{ position: "relative" }}>
                <Controller
                  control={control}
                  rules={{ required: "Заполните это поле" }}
                  name='date'
                  render={({ field }) => (
                    <>
                      <input
                        type='date'
                        disabled={
                          currentStatus.id === 3 ||
                          currentStatus.id === 6 ||
                          timeExpired
                        }
                        min={currentDate.toISOString().split("T")[0]}
                        defaultValue={bookingData.booking.start?.slice(0, 10)}
                        value={
                          field.value
                            ? new Date(field.value).toISOString().slice(0, 10)
                            : bookingData.booking.start?.slice(0, 10) || ""
                        }
                        onChange={(e) => {
                          field.onChange(e.target.value)
                          onDateChange(e.target.value, field)
                        }}
                        className='inputOutlined mt-1 w-full text-left pl-10 pr-2'
                        style={{
                          paddingLeft: "40px",
                          // iOS Safari fixes
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                          appearance: "none",
                          // Ensure proper height and alignment
                          height: "48px",
                          lineHeight: "48px",
                          boxSizing: "border-box",
                          // iOS specific formatting
                          fontFamily:
                            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                          // Force left alignment
                          textAlign: "left",
                          textAlignLast: "left",
                        }}
                        // iOS Safari specific attributes
                        autoComplete='off'
                        autoCorrect='off'
                        autoCapitalize='off'
                        spellCheck='false'
                        // iOS specific pattern for date format
                        pattern='\\d{2}\\.\\d{2}\\.\\d{4}'
                      />
                      <div
                        className='sm:hidden'
                        style={{
                          position: "absolute",
                          left: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          zIndex: 1,
                        }}
                      >
                        <svg
                          width='24'
                          height='24'
                          viewBox='0 0 24 24'
                          fill='none'
                        >
                          <g clipPath='url(#clip0_8637_17044)'>
                            <path
                              d='M17 3H21C21.2652 3 21.5196 3.10536 21.7071 3.29289C21.8946 3.48043 22 3.73478 22 4V20C22 20.2652 21.8946 20.5196 21.7071 20.7071C21.5196 20.8946 21.2652 21 21 21H3C2.73478 21 2.48043 20.8946 2.29289 20.7071C2.10536 20.5196 2 20.2652 2 20V4C2 3.73478 2.10536 3.48043 2.29289 3.29289C2.48043 3.10536 2.73478 3 3 3H7V1H9V3H15V1H17V3ZM15 5H9V7H7V5H4V9H20V5H17V7H15V5ZM20 11H4V19H20V11Z'
                              fill='#A4AFBD'
                            />
                          </g>
                          <defs>
                            <clipPath id='clip0_8637_17044'>
                              <rect width='24' height='24' fill='white' />
                            </clipPath>
                          </defs>
                        </svg>
                      </div>
                    </>
                  )}
                />
              </div>

              {errors.date && (
                <p className='text-red'>{errors?.date?.message}</p>
              )}
              {isDayOff && (
                <p className='text-red text-sm mt-1'>
                  {timeSlotsError?.data?.message}
                </p>
              )}
            </label>

            <label className='label-base relative sm:ml-2'>
              <span className='font-medium text-main'>Время</span>
              <Controller
                control={control}
                name='time'
                rules={{ required: "Заполните это поле" }}
                render={({ field }) => (
                  <div className='relative w-full'>
                    <div className='absolute left-2 top-[14px] z-10 pointer-events-none'>
                      <AccessTimeIcon color='disabled' />
                    </div>
                    <input
                      type='time'
                      step='60'
                      className='inputOutlined mt-[3px] w-full pl-10 pr-2'
                      style={{
                        paddingLeft: "40px",
                        // iOS Safari fixes
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        appearance: "none",
                        // Ensure proper height and alignment
                        height: "48px",
                        lineHeight: "48px",
                        boxSizing: "border-box",
                        // iOS specific formatting
                        fontFamily:
                          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        // Force left alignment
                        textAlign: "left",
                        textAlignLast: "left",
                      }}
                      disabled={isTimeDisabled}
                      min={
                        timeSlotsData && timeSlotsData.length > 0
                          ? timeSlotsData[0]
                          : undefined
                      }
                      max={
                        timeSlotsData && timeSlotsData.length > 0
                          ? timeSlotsData[timeSlotsData.length - 1]
                          : undefined
                      }
                      list='time-slots-list'
                      value={
                        field.value
                          ? field.value
                          : time
                            ? time
                            : bookingData?.booking?.start?.slice(-5) || ""
                      }
                      onChange={(e) => {
                        field.onChange(e.target.value)
                        setTime(e.target.value)
                        setTimeChange(true)
                      }}
                      // iOS Safari specific attributes
                      autoComplete='off'
                      autoCorrect='off'
                      autoCapitalize='off'
                      spellCheck='false'
                      // iOS specific pattern for time format
                      pattern='\\d{2}:\\d{2}'
                    />
                    <datalist id='time-slots-list'>
                      {timeSlotsData && timeSlotsData.length > 0
                        ? timeSlotsData.map((slot) => (
                            <option key={slot} value={slot}>
                              {slot}
                            </option>
                          ))
                        : null}
                    </datalist>
                  </div>
                )}
              />
              {errors.time && <p className='text-red'>{errors.time.message}</p>}

              {/* {time && (!timeSlotsData || timeSlotsData.length === 0) && (
                <p className='text-red text-sm mt-1'>
                  На выбранную дату нет доступного времени
                </p>
              )} */}
            </label>
          </div>

          {/* Предупреждение о предоплате с таймером - показывается только для статуса "ожидание оплаты" */}
          {hasPrepaid && currentStatus.id === 11 && !timeExpired && (
            <div className='bg-[#FFF3CD] border border-[#FFEAA7] rounded-lg p-4 mb-4'>
              <div className='flex items-center gap-2 mb-2'>
                <svg
                  className='w-5 h-5 text-[#856404]'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                    clipRule='evenodd'
                  />
                </svg>
                <span className='font-medium text-[#856404]'>Внимание!</span>
              </div>
              <p className='text-[#856404] text-sm mb-2'>
                Успейте подтвердить запись предоплатой в течение{" "}
                <strong>{formatTime(timeLeft)} минут</strong>, и ваше место
                будет закреплено!
              </p>
            </div>
          )}

          {/* Сообщение об истечении времени */}
          {hasPrepaid && currentStatus.id === 11 && timeExpired && (
            <div className='bg-[#FEE2E2] border border-[#FECACA] rounded-lg p-4 mb-4'>
              <div className='flex items-center gap-2 mb-2'>
                <svg
                  className='w-5 h-5 text-[#DC2626]'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                    clipRule='evenodd'
                  />
                </svg>
                <span className='font-medium text-[#DC2626]'>
                  Время истекло!
                </span>
              </div>
              <p className='text-[#DC2626] text-sm mb-2'>
                Вы не успели сделать предоплату в течение 15 минут.
                <br />
                <strong>Создайте новую запись</strong> для бронирования времени.
              </p>
            </div>
          )}

          <div className='flex flex-col justify-between gap-2   '>
            <label className='label-base relative sm:ml-2'>
              <span>Статус</span>
            </label>
            <span
              className={`rounded-lg w-full py-3 text-left px-4 items-center mb-6 font-[400] text-medium h-[48px]
                      ${
                        !currentStatus
                          ? ""
                          : currentStatus.id === 4
                            ? "text-[#92400E] bg-[#FEF3C7]"
                            : currentStatus.id === 3
                              ? "text-[#92400E] bg-[#FEE2E2]"
                              : currentStatus.id === 8
                                ? "bg-[#edf1f7]"
                                : currentStatus.id === 11
                                  ? "text-[#EE6F42] bg-[#FFF4ED]"
                                  : "bg-[#D1FAE5] text-[#065F46]"
                      }`}
            >
              {currentStatus && ` ${getStatusText(currentStatus)}`}
            </span>

            {!(
              currentStatus.id === 3 ||
              currentStatus.id === 6 ||
              currentStatus.id === 8
            ) &&
              !timeExpired &&
              (hasPrepaid ? (
                <button
                  type='button'
                  className={`px-4 py-2 w-full rounded-lg text-white ${
                    isPaymentProcessing || dateChange || timeChange
                      ? "bg-darkGray cursor-not-allowed"
                      : "bg-darkOrange"
                  }`}
                  onClick={handlePayment}
                  disabled={isPaymentProcessing || dateChange || timeChange}
                >
                  {isPaymentProcessing ? "Обработка..." : "Оплатить"}
                </button>
              ) : (
                <button
                  type='button'
                  className={`px-4 py-2 w-full rounded-lg text-white ${dateChange || timeChange ? "bg-darkGray" : "bg-darkOrange"}`}
                  onClick={() => setShowConfirm(true)}
                  disabled={dateChange || timeChange}
                >
                  Подтвердить
                </button>
              ))}
            {/* Кнопка "Сохранить изменения" показывается только при изменении даты или времени и если время не истекло */}
            {(dateChange || timeChange) && !timeExpired && (
              <button
                type='button'
                disabled={
                  isDayOff ||
                  (time &&
                    timeSlotsData &&
                    timeSlotsData.length > 0 &&
                    !timeSlotsData.includes(time))
                }
                className={`px-4 py-2 w-full border rounded-lg border-darkGray font-medium ${
                  isDayOff ||
                  (time &&
                    timeSlotsData &&
                    timeSlotsData.length > 0 &&
                    !timeSlotsData.includes(time))
                    ? "border-darkGray text-darkGray cursor-not-allowed"
                    : "text-darkOrange border-darkOrange"
                }`}
                onClick={() => setShowEdit(true)}
              >
                Сохранить изменения
              </button>
            )}
            {/* Кнопка "Отменить запись" скрыта только если запись уже отменена */}
            {currentStatus.id !== 3 && (
              <button
                type='button'
                disabled={currentStatus.id === 6}
                className={`px-4 py-2 w-full rounded-lg font-medium ${currentStatus.id === 6 && " text-darkGray"}`}
                onClick={() => setShowCancel(true)}
              >
                Отменить запись
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showConfirm && (
        <ChangeStatus
          setActive={setShowConfirm}
          onClose={() => setShowConfirm(false)}
          active={showConfirm}
          onConfirm={confirmBooking}
          closeStatusModal={() => setShowConfirm(false)}
          currentStatusId={statusId}
          text={"Вы уверены что хотите подтвердить запись?"}
          isSuccess={isSuccess}
          successLabel={"Ваша запись успешно подтверждена"}
          errorLabel={"При подтверждении произошла ошибка"}
        />
      )}
      {showCancel && (
        <ChangeStatus
          isError={isError}
          isSuccess={isSuccess}
          setActive={setShowCancel}
          onClose={() => setShowCancel(false)}
          active={showCancel}
          onConfirm={cancelBooking}
          closeStatusModal={() => setShowCancel(false)}
          currentStatusId={statusId}
          text={"Вы уверены что хотите отменить запись?"}
          successLabel={"Ваша запись успешно отменена"}
          errorLabel={"При отмене произошла ошибка"}
        />
      )}
      {showEdit && (
        <ChangeDate
          closeStatusModal={() => setShowEdit(false)}
          onClose={() => setShowEdit(false)}
          active={() => setShowEdit(true)}
          onConfirm={handleSubmit(editBooking)}
          currentStatusId={statusId}
          isSuccess={isSuccess}
          isError={isError}
          text={"Вы уверены, что хотите перенести визит?"}
        />
      )}
    </form>
  )
}

export default ClientConfirmation